import { z } from "zod";

export const BOUNDS = {
  reasonChars: 160,
  shortLabelChars: 64,
  preferenceTextChars: 80,
  queryChars: 160,
  intentChars: 240,
  recentInterests: 6,
  preferenceRecords: 40,
  prioritizedFacts: 6,
  interestAttributes: 6,
  valuesPerInterestAttribute: 5,
  searchResultsPerPage: 24,
  compareCandidates: 4,
  compareCriteria: 8,
  collectionRoles: 8,
  collectionItems: 16,
} as const;

const sensitivePattern = /(?:\b(?:password|passcode|api[ _-]?key|auth(?:entication|orization)?[ _-]?token|cookie|credit[ _-]?card|bank[ _-]?account|social[ _-]?security|passport|diagnos(?:is|ed)|medication|religion|political[ _-]?affiliation|sexual[ _-]?orientation|transcript|raw[ _-]?memory|hidden[ _-]?prompt)\b|\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b|\b(?:\+?\d[\d ()-]{7,}\d)\b|<\/?(?:script|style|iframe)|javascript:)/iu;

export class ProductistValidationError extends Error {
  constructor(public readonly code: "VALIDATION_ERROR" | "UNSUPPORTED_FIELD" | "SENSITIVE_DATA_REJECTED", message: string, public readonly field?: string) {
    super(message);
    this.name = "ProductistValidationError";
  }
}

export function normalizeText(value: string, max: number, field = "text"): string {
  const normalized = value.normalize("NFKC").trim().replace(/\s+/gu, " ");
  if (normalized.length === 0 || normalized.length > max) {
    throw new ProductistValidationError("VALIDATION_ERROR", `${field} must contain 1 to ${max} characters.`, field);
  }
  return normalized;
}

export function assertSafeText(value: string, field = "text"): void {
  if (sensitivePattern.test(value)) {
    throw new ProductistValidationError("SENSITIVE_DATA_REJECTED", "The shared personalization included data that Productist does not store.", field);
  }
  if (/https?:\/\//iu.test(value)) {
    throw new ProductistValidationError("SENSITIVE_DATA_REJECTED", "URLs are not accepted in shared personalization text.", field);
  }
}

const safeText = (max: number, field: string) => z.string().transform((value, context) => {
  try {
    const normalized = normalizeText(value, max, field);
    assertSafeText(normalized, field);
    return normalized;
  } catch (error) {
    context.addIssue({ code: "custom", message: error instanceof Error ? error.message : "Invalid text" });
    return z.NEVER;
  }
});

export const requestIdSchema = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,63}$/u);
export const countrySchema = z.string().regex(/^[A-Z]{2}$/u);
export const currencySchema = z.string().regex(/^[A-Z]{3}$/u);
export const languageSchema = z.string().min(2).max(35).regex(/^[A-Za-z]{2,8}(?:-[A-Za-z0-9]{1,8})*$/u);
export const gidSchema = z.string().min(1).max(512).regex(/^gid:\/\/shopify\//u);

export const mutationControlSchema = z.object({
  requestId: requestIdSchema,
  expectedStateVersion: z.number().int().nonnegative().safe().optional(),
}).strict();

export const uiPreferencesSchema = z.object({
  density: z.enum(["comfortable", "compact"]).optional(),
  contentBalance: z.enum(["visual", "balanced", "specification_led"]).optional(),
  comparisonStyle: z.enum(["discovery_first", "compare_first"]).optional(),
  theme: z.enum(["system", "light", "dark"]).optional(),
  motion: z.enum(["system", "reduced"]).optional(),
  prioritizedFacts: z.array(z.enum(["price", "dimensions", "material", "delivery", "rating", "seller", "compatibility", "policy", "availability", "condition"])).max(BOUNDS.prioritizedFacts).refine((items) => new Set(items).size === items.length, "Facts must be unique.").optional(),
}).strict().refine((value) => Object.keys(value).length > 0, "At least one interface preference is required.");

export const provenanceSchema = z.object({
  source: z.enum(["user", "agent_shared_memory", "current_conversation", "session_inference", "demo_fixture"]),
  scope: z.enum(["action", "session", "saved_local"]),
  reason: safeText(BOUNDS.reasonChars, "reason"),
  confidence: z.number().finite().min(0).max(1).optional(),
}).strict();

const moneyRangeSchema = z.object({
  currency: currencySchema,
  min: z.number().int().nonnegative().safe().optional(),
  max: z.number().int().nonnegative().safe().optional(),
}).strict().refine((value) => value.min !== undefined || value.max !== undefined, "A minimum or maximum is required.").refine((value) => value.min === undefined || value.max === undefined || value.min <= value.max, "Minimum must not exceed maximum.");

const interestAttributeSchema = z.object({
  name: z.enum(["brand", "material", "color", "style", "size", "compatibility", "condition"]),
  values: z.array(safeText(BOUNDS.preferenceTextChars, "attribute value")).min(1).max(BOUNDS.valuesPerInterestAttribute).refine((values) => new Set(values.map((value) => value.toLocaleLowerCase())).size === values.length, "Values must be unique."),
}).strict();

export const recentInterestSchema = z.object({
  label: safeText(BOUNDS.shortLabelChars, "interest label"),
  query: safeText(BOUNDS.queryChars, "interest query"),
  department: z.enum(["electronics", "home_garden", "kitchen_dining", "sports_outdoors", "fashion", "beauty_personal_care", "travel_luggage", "office", "toys_hobbies", "pet_supplies"]).optional(),
  attributes: z.array(interestAttributeSchema).max(BOUNDS.interestAttributes).refine((items) => new Set(items.map((item) => item.name)).size === items.length, "Attribute names must be unique.").optional(),
  strength: z.enum(["active", "exploring", "fading"]),
  recency: z.enum(["today", "this_week", "this_month", "older"]),
  budget: moneyRangeSchema.optional(),
  destinationCountry: countrySchema.optional(),
  reason: safeText(BOUNDS.reasonChars, "reason"),
  scope: z.enum(["session", "saved_local"]).optional(),
  confidence: z.number().finite().min(0).max(1).optional(),
}).strict();

const textRuleSchema = z.object({
  field: z.enum(["category", "brand", "material", "color", "style", "size", "compatibility", "merchant_region"]),
  value: safeText(BOUNDS.preferenceTextChars, "preference value"),
  strength: z.enum(["required", "preferred", "avoid"]),
}).strict();
const conditionRuleSchema = z.object({ field: z.literal("condition"), value: z.enum(["new", "secondhand"]), strength: z.enum(["required", "preferred", "avoid"]) }).strict();
const priceRuleSchema = z.object({ field: z.enum(["price_min", "price_max"]), value: z.object({ amount: z.number().int().nonnegative().safe(), currency: currencySchema }).strict(), strength: z.enum(["required", "preferred"]) }).strict();
const countryRuleSchema = z.object({ field: z.literal("shipping_country"), value: countrySchema, strength: z.enum(["required", "preferred"]) }).strict();
const urgencyRuleSchema = z.object({ field: z.literal("delivery_urgency"), value: z.enum(["no_rush", "standard", "urgent"]), strength: z.enum(["required", "preferred"]) }).strict();
const localeRuleSchema = z.union([
  z.object({ field: z.literal("currency"), value: currencySchema, strength: z.enum(["required", "preferred"]) }).strict(),
  z.object({ field: z.literal("language"), value: languageSchema, strength: z.enum(["required", "preferred"]) }).strict(),
]);
export const preferenceRuleSchema = z.union([textRuleSchema, conditionRuleSchema, priceRuleSchema, countryRuleSchema, urgencyRuleSchema, localeRuleSchema]);

const searchFiltersSchema = z.object({
  categoryIds: z.array(z.string().min(1).max(100)).min(1).max(8).optional(),
  price: moneyRangeSchema.optional(),
  available: z.boolean().optional(),
  shopIds: z.array(gidSchema).min(1).max(20).optional(),
  condition: z.array(z.enum(["new", "secondhand"])).min(1).max(2).optional(),
  shipsToCountry: countrySchema.optional(),
  shipsFromCountries: z.array(countrySchema).min(1).max(5).optional(),
  attributes: z.array(z.object({ name: z.enum(["Color", "Size", "Target gender"]), values: z.array(safeText(80, "attribute value")).min(1).max(10) }).strict()).min(1).max(10).optional(),
  rating: z.object({ min: z.number().min(0).max(5).optional(), minCount: z.number().int().min(0).max(2_147_483_647).optional() }).strict().optional(),
  priceTiers: z.array(z.enum(["low", "medium", "high"])).min(1).max(3).optional(),
}).strict();

export const searchCriteriaSchema = z.object({
  query: z.string().transform((value, context) => {
    try { return normalizeText(value, BOUNDS.queryChars, "query"); }
    catch (error) { context.addIssue({ code: "custom", message: error instanceof Error ? error.message : "Invalid query" }); return z.NEVER; }
  }),
  context: z.object({
    addressCountry: countrySchema.optional(),
    language: languageSchema.optional(),
    currency: currencySchema.optional(),
    intent: z.string().transform((value, context) => {
      try { return normalizeText(value, BOUNDS.intentChars, "intent"); }
      catch (error) { context.addIssue({ code: "custom", message: error instanceof Error ? error.message : "Invalid intent" }); return z.NEVER; }
    }).optional(),
  }).strict().optional(),
  filters: searchFiltersSchema.optional(),
  sort: z.enum(["best_match", "price_asc", "price_desc", "rating_desc"]).optional(),
  page: z.union([
    z.object({ mode: z.literal("first"), limit: z.number().int().min(1).max(BOUNDS.searchResultsPerPage).optional() }).strict(),
    z.object({ mode: z.literal("next"), cursor: z.string().min(1).max(512), limit: z.number().int().min(1).max(BOUNDS.searchResultsPerPage).optional() }).strict(),
  ]).optional(),
}).strict();

export function parseStrict<T>(schema: z.ZodType<T>, input: unknown): T {
  const result = schema.safeParse(input);
  if (!result.success) {
    const issue = result.error.issues[0];
    const unsupported = issue?.code === "unrecognized_keys";
    throw new ProductistValidationError(unsupported ? "UNSUPPORTED_FIELD" : "VALIDATION_ERROR", unsupported ? "The input contains an unsupported field." : (issue?.message ?? "The input is invalid."), issue?.path.join("."));
  }
  return result.data;
}
